// /frontend/src/components/layout/Header.jsx
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { FaHeart, FaShoppingCart, FaShoppingBag, FaPencilRuler, FaBoxOpen, FaBed, FaAngleDown, FaSearch, FaChevronLeft, FaUser } from 'react-icons/fa';
import logo from '../../assets/logo.png';

const Header = () => {
  const { cartItems } = useSelector((state) => state.cart) || { cartItems: [] };
  const cartItemsCount = cartItems?.length || 0;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get authentication state from Redux store
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  
  // Get current path to determine active tab
  const currentPath = location?.pathname || '';

  // State to track active category
  const [activeCategory, setActiveCategory] = useState('all');
  
  // State for dropdowns and mobile menu
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  
  // Animation states
  const [animateCart, setAnimateCart] = useState(false);
  
  // Determine if the current path is a browse-related page
  const isBrowsePage = () => {
    return currentPath === '/' || 
           currentPath === '/browse' || 
           currentPath.startsWith('/category/') || 
           currentPath.startsWith('/product/');
  };
  
  // Category data with enhanced descriptions
  const categories = [
    { 
      id: 'all', 
      name: 'All',
      featured: false
    },
    { 
      id: 'bench', 
      name: 'Bench',
      featured: false
    },
    { 
      id: 'stool', 
      name: 'Stool',
      featured: true 
    },
    { 
      id: 'chair', 
      name: 'Chair',
      featured: true
    },
    { 
      id: 'table', 
      name: 'Table',
      featured: true
    },
    { 
      id: 'cabinet', 
      name: 'Cabinet',
      featured: false
    },
    { 
      id: 'sofa', 
      name: 'Sofa',
      featured: true
    },
    { 
      id: 'ottoman', 
      name: 'Ottoman',
      featured: false
    },
    { 
      id: 'bed', 
      name: 'Bed',
      featured: false
    },
    { 
      id: 'dresser', 
      name: 'Dresser',
      featured: false
    },
    { 
      id: 'desk', 
      name: 'Desk',
      featured: false
    },
    { 
      id: 'settee', 
      name: 'Settee',
      featured: false
    }
  ];
  
  // Set default category to all if none is active
  useEffect(() => {
    let categoryActive = false;
    
    for (const category of categories) {
      if (currentPath.includes(`/category/${category.id}`)) {
        setActiveCategory(category.id);
        categoryActive = true;
        break;
      }
    }
    
    // Default to all if no category is active and it's a browse-related page
    if (!categoryActive && isBrowsePage()) {
      setActiveCategory('all');
    }
  }, [currentPath, categories]);
  
  
  // Active tab highlighting
  const isActive = (path) => {
    // Special case for Collection - should be active for category pages too
    if (path === '/browse' && (
      currentPath.includes('/category/') || 
      currentPath === '/' || 
      currentPath.includes('/product/')
    )) {
      return true;
    }
    return currentPath.includes(path);
  };
  
  // Simulate cart animation when component mounts or when cart updates
  useEffect(() => {
    setAnimateCart(true);
    setTimeout(() => setAnimateCart(false), 1000);
  }, [cartItemsCount]);
  
  // Handle logout
  const handleLogout = () => {
    dispatch(logout()).finally(() => {
      // Refresh halaman setelah logout selesai (berhasil atau gagal)
      window.location.reload();
    });
  };
  
  // Handle search submit - navigate to category with search param
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Close the search dropdown
      setIsSearchOpen(false);
      // Navigate to category page with search parameter
      navigate(`/category/all?search=${encodeURIComponent(searchQuery.trim())}`);
      // Clear search input
      setSearchQuery('');
    }
  };
  
  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
      if (isSearchOpen && !event.target.closest('.search-container')) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isSearchOpen]);
  
  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      {/* Announcement bar */}
      <div className="bg-teal-800 text-white py-2 text-center text-sm font-medium">
        <div className="max-w-7xl mx-auto px-4">
          New Spring Collection Just Arrived • Complimentary Designer Support • Virtual Design Consultations
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img src={logo} alt="Henderson Design Group" className="h-12 transform hover:scale-105 transition-transform duration-300" />
            </Link>
          </div>
          
          {/* Main Navigation - Enhanced with better styling and indicators */}
          <nav className="hidden md:flex space-x-4">
                        <Link 
              to="/designer-packages" 
              className={`relative group flex items-center px-6 py-3 rounded-lg transition-all duration-300 overflow-hidden ${
                isActive('/designer-packages') 
                  ? 'text-white font-medium bg-gradient-to-r from-teal-700 to-teal-800 shadow-md' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="relative z-10 flex items-center">
                <FaBoxOpen className={`mr-2.5 ${isActive('/designer-packages') ? 'text-white' : 'text-gray-500 group-hover:text-teal-800'} transition-colors duration-300`} />
                <span className={`group-hover:text-teal-800 transition-colors duration-300 ${isActive('/designer-packages') ? '!text-white' : ''}`}>Furniture Packages</span>
              </div>
              {!isActive('/designer-packages') && (
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-teal-600 to-teal-800 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
              )}
              
              {/* Enhanced Dropdown */}
              <div className="hidden group-hover:block absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-80 bg-white shadow-lg rounded-md border border-gray-100 z-10 overflow-hidden transition-all duration-300">
                <div className="p-4 border-b border-gray-100">
                  <strong className="block text-teal-800 text-lg mb-1">Furniture Packages</strong>
                  <p className="text-sm text-gray-600">Expertly curated packages for immediate purchase</p>
                </div>
                
                <div className="grid grid-cols-2 gap-2 p-4">
                  <Link to="/designer-packages/minimalist" className="block p-3 text-center bg-gray-50 hover:bg-teal-50 rounded-md group transition-colors">
                    <span className="block text-teal-800 font-medium mb-1 group-hover:text-teal-900">Minimalist</span>
                    <span className="text-xs text-gray-500">from $1,999</span>
                  </Link>
                  
                  <Link to="/designer-packages/scandinavian" className="block p-3 text-center bg-gray-50 hover:bg-teal-50 rounded-md group transition-colors">
                    <span className="block text-teal-800 font-medium mb-1 group-hover:text-teal-900">Scandinavian</span>
                    <span className="text-xs text-gray-500">from $2,499</span>
                  </Link>
                  
                  <Link to="/designer-packages/modern" className="block p-3 text-center bg-gray-50 hover:bg-teal-50 rounded-md group transition-colors">
                    <span className="block text-teal-800 font-medium mb-1 group-hover:text-teal-900">Modern</span>
                    <span className="text-xs text-gray-500">from $2,199</span>
                  </Link>
                  
                  <Link to="/designer-packages/luxury" className="block p-3 text-center bg-gray-50 hover:bg-teal-50 rounded-md group transition-colors">
                    <span className="block text-teal-800 font-medium mb-1 group-hover:text-teal-900">Luxury</span>
                    <span className="text-xs text-gray-500">from $3,999</span>
                  </Link>
                </div>
                
                <Link to="/designer-packages" className="block p-3 text-center text-teal-800 hover:bg-teal-50 font-medium text-sm">
                  View All Furniture Packages
                </Link>
              </div>
            </Link>
            
            <Link 
              to="/custom-design" 
              className={`relative group flex items-center px-6 py-3 rounded-lg transition-all duration-300 overflow-hidden ${
                isActive('/custom-design') 
                  ? 'text-white font-medium bg-gradient-to-r from-teal-700 to-teal-800 shadow-md' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="relative z-10 flex items-center">
                <FaPencilRuler className={`mr-2.5 ${isActive('/custom-design') ? 'text-white' : 'text-gray-500 group-hover:text-teal-800'} transition-colors duration-300`} />
                <span className={`group-hover:text-teal-800 transition-colors duration-300 ${isActive('/custom-design') ? '!text-white' : ''}`}>Design</span>
              </div>
              {!isActive('/custom-design') && (
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-teal-600 to-teal-800 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
              )}
              
              {/* Enhanced Dropdown */}
              <div className="hidden group-hover:block absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-80 bg-white shadow-lg rounded-md border border-gray-100 z-10 overflow-hidden transition-all duration-300">
                <div className="p-4 border-b border-gray-100">
                  <strong className="block text-teal-800 text-lg mb-1">Design Service</strong>
                  <p className="text-sm text-gray-600">Work with our professional designers to create your perfect space</p>
                </div>
                
                <div className="p-4 bg-gray-50">
                  <div className="flex items-start mb-4">
                    <div className="bg-teal-100 text-teal-800 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-1">1</div>
                    <div>
                      <strong className="block text-gray-800 mb-1">Initial Consultation</strong>
                      <p className="text-xs text-gray-600">Share your vision and requirements with our team</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start mb-4">
                    <div className="bg-teal-100 text-teal-800 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-1">2</div>
                    <div>
                      <strong className="block text-gray-800 mb-1">Design</strong>
                      <p className="text-xs text-gray-600">Receive personalized designs and 3D visualizations</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-teal-100 text-teal-800 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-1">3</div>
                    <div>
                      <strong className="block text-gray-800 mb-1">Revisions & Finalization</strong>
                      <p className="text-xs text-gray-600">Refine your design with unlimited revisions</p>
                    </div>
                  </div>
                </div>
                
                <Link to="/custom-design" className="block p-3 text-center text-teal-800 hover:bg-teal-50 font-medium text-sm">
                  Learn More About Design
                </Link>
              </div>
            </Link>

                        <Link 
              to="/browse" 
              className={`relative group flex items-center px-6 py-3 rounded-lg transition-all duration-300 overflow-hidden ${
                isActive('/browse') 
                  ? 'text-white font-medium bg-gradient-to-r from-teal-700 to-teal-800 shadow-md' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaShoppingBag className={`mr-2.5 ${isActive('/browse') ? 'text-white' : 'text-gray-500 group-hover:text-teal-800'} transition-colors duration-300`} />
              <span className={`group-hover:text-teal-800 transition-colors duration-300 ${isActive('/browse') ? '!text-white' : ''}`}>Collection</span>
              {!isActive('/browse') && (
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-teal-600 to-teal-800 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
              )}
              
              {/* Dropdown Preview */}
              <div className="hidden group-hover:block absolute top-full left-0 mt-2 w-64 p-4 bg-white shadow-lg rounded-md border border-gray-100 z-10 text-sm text-gray-600 transition-all duration-300">
                <div className="flex justify-between mb-2">
                  <strong className="block text-teal-800">Categories</strong>
                  <span className="text-xs text-teal-600 hover:underline">See all</span>
                </div>
                {categories.map(cat => (
                  <Link 
                    key={cat.id} 
                    to={`/category/${cat.id}`}
                    className="flex justify-between items-center p-2 hover:bg-gray-50 rounded"
                  >
                    <span>{cat.name}</span>
                  </Link>
                ))}
              </div>
            </Link>
          
          </nav>
          
          {/* User Actions - Enhanced with better hover effects */}
          <div className="flex items-center space-x-4">
            {/* Search button and dropdown */}
            <div className="search-container relative">
              <button 
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="group relative p-2 text-gray-700 transition-colors duration-300"
              >
                <FaSearch className="h-5 w-5 group-hover:text-teal-800 transition-colors duration-300" />
                <span className="absolute inset-0 rounded-full group-hover:bg-teal-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </button>
              
              {/* Search dropdown - FIXED SEARCH FUNCTIONALITY */}
              {isSearchOpen && (
                <div className="absolute top-full right-0 mt-4 mr-4 w-80 bg-white shadow-lg rounded-md p-4 z-20 border border-gray-200">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="relative">
                      <input 
                        type="search" 
                        placeholder="Search for furniture..." 
                        className="w-full border-gray-300 rounded-md pl-10 pr-4 py-2 focus:ring-teal-500 focus:border-teal-500" 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                      />
                      <FaSearch className="absolute left-3 top-3 text-gray-400" />
                      <button 
                        type="submit" 
                        className="absolute right-2 top-2 bg-teal-800 text-white p-1 rounded hover:bg-teal-900"
                      >
                        Go
                      </button>
                    </div>
                  </form>
                  <div className="mt-2 text-xs text-gray-500">
                    Popular: Dining Tables, Counter Stools, Sofas
                  </div>
                </div>
              )}
            </div>
            
            {/* Wishlist */}
            <Link to={isAuthenticated ? "/wishlist" : "/login?redirect=/wishlist"} className="group relative p-2 text-gray-700 transition-colors duration-300">
              <FaHeart className={`h-5 w-5 group-hover:text-pink-500 transition-all duration-300 transform ${animateCart ? 'scale-110' : ''}`} />
              <span className="absolute inset-0 rounded-full group-hover:bg-pink-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
            </Link>
            
            {/* Shopping Cart with animation */}
            <Link to={isAuthenticated ? "/cart" : "/login?redirect=/cart"} className="group relative p-2 text-gray-700 transition-colors duration-300">
              <FaShoppingCart className={`h-5 w-5 group-hover:text-teal-800 transition-all duration-300 transform ${animateCart ? 'scale-110' : ''}`} />
              <span className="absolute inset-0 rounded-full group-hover:bg-teal-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              {cartItemsCount > 0 && (
                <span className={`absolute -top-2 -right-2 bg-teal-800 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs font-bold shadow-md ${animateCart ? 'animate-pulse' : ''}`}>
                  {cartItemsCount}
                </span>
              )}
            </Link>
            
            {/* User Menu - Only shown when authenticated */}
            {isAuthenticated ? (
              <div className="user-menu-container relative">
                <button 
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="relative group overflow-hidden rounded-full h-10 w-10 bg-teal-100 flex items-center justify-center border-2 hover:border-teal-800 transition-colors duration-300"
                >
                  <FaUser className="h-5 w-5 text-teal-800" />
                </button>
                
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white shadow-lg rounded-md border border-gray-100 z-50 py-1">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.firstName} {user?.lastName}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    
                    <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      My Profile
                    </Link>
                    <Link to="/account/orders" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      My Collection Orders
                    </Link>
                    <Link to="/account/design-requests" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      My Design Orders
                    </Link>
                    
                    {user?.role === 'admin' && (
                      <Link to="/admin" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        Admin Dashboard
                      </Link>
                    )}
                    
                    <div className="border-t border-gray-100 mt-1">
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Login Button */}
                <Link to="/login" className="relative overflow-hidden px-5 py-2 border-2 border-teal-800 text-teal-800 rounded-lg font-medium transition-all duration-300 hover:shadow-md group">
                  <span className="relative z-10">Login</span>
                  <span className="absolute inset-0 bg-teal-50 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
                </Link>
                
                {/* Register Button */}
                <Link to="/register" className="relative overflow-hidden px-5 py-2.5 bg-gradient-to-r from-teal-700 to-teal-800 text-white rounded-lg font-medium transition-all duration-300 hover:shadow-md group">
                  <span className="relative z-10">Register</span>
                  <span className="absolute inset-0 bg-gradient-to-r from-teal-800 to-teal-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Elegant Category Navigation - Only shown for Collection pages */}
      {isBrowsePage() && (
        <div className="border-t border-gray-200">
          <div className="max-w-7xl mx-auto">
            {/* Elegant, minimalistic category navigation */}
            <div className="relative px-4">
              <div className="flex items-center justify-center overflow-x-auto scrollbar-hide">
                <div className="flex items-center space-x-8 py-4">
                  {categories.map(category => (
                    <Link 
                      key={category.id}
                      to={`/category/${category.id}`}
                      className="group"
                      onClick={() => setActiveCategory(category.id)}
                    >
                      <div className="relative">
                        {/* Category Name */}
                        <span className={`
                          block px-1 font-light tracking-wide transition-all duration-300 text-base uppercase
                          ${activeCategory === category.id ? 'text-teal-800 font-medium' : 'text-gray-500 group-hover:text-gray-800'}
                        `}>
                          {category.name}
                        </span>
                        
                        {/* Animated bottom line for active/hover state */}
                        <span 
                          className={`
                            absolute bottom-0 left-0 w-full h-0.5 bg-teal-800 transform origin-left transition-transform duration-300 ease-out
                            ${activeCategory === category.id ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}
                          `}
                        ></span>
                        
                        {/* Removed featured dot indicator as requested */}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              
              {/* Subtle gradient overlay indicators */}
              <div className="absolute top-0 right-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent pointer-events-none opacity-80"></div>
              <div className="absolute top-0 left-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent pointer-events-none opacity-80"></div>
            </div>
            
            {/* Decorative line under categories */}
            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
          </div>
        </div>
      )}
      
    </header>
  );
};

export default Header;